import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import { Loader } from 'packages/workspace/design/widgets/ui/Loader';
import useGetConversation from 'packages/workspace/modules/datastores/internal-chat/useGetConversation';
import type { IOnlineUser } from 'packages/workspace/services/models/account';
import useInternalConversationStore, { InternalConversationStore } from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

interface IOnlineUsersContainerProps {
  users: Array<IOnlineUser>;
}

export const OnlineUsersContainer: React.FC<IOnlineUsersContainerProps> = ({ users }) => {
  const [activeUserConv, SetActiveUserConv] = useState<string>('');
  const conversation = useGetConversation(activeUserConv);
  const internalConvState = useInternalConversationStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    SetActiveUserConv((prevState) => {
      if (internalConvState.active_window?.conversation_id !== prevState) {
        return '';
      }
      return prevState;
    });
  }, [internalConvState.active_window]);

  useMemo(() => {
    if (conversation.data?.conversation_id && conversation.data.conversation_id !== internalConvState.active_window?.conversation_id) {
      InternalConversationStore.setActiveWindow(conversation.data.conversation_id);
    }
  }, [conversation.data?.conversation_id]);

  /**
   * Click to inititate conversation
   * Get conversationId - if not already interacted/ call Backend.
   * @param recipientOperatorId
   */
  const initiateConversation = async (recipientOperatorId: string) => {
    SetActiveUserConv(recipientOperatorId);
    queryClient.setQueryData<IOnlineUser[]>(['onlineUsers'], (prevOnlineUsers) => {
      if (prevOnlineUsers === undefined) prevOnlineUsers = [] as IOnlineUser[];
      return prevOnlineUsers.map((entity: IOnlineUser) => {
        if (entity.id === recipientOperatorId) {
          const msgsCount = internalConvState.ongoing_messages?.[entity.conversation_id]?.filter((eachMessage) => eachMessage.isread === false)?.length ?? 0;
          InternalConversationStore.updateUnreadMessagesToZero(msgsCount + (entity.unReadMessagesCount ?? 0), true);
          entity = {
            ...entity,
            unReadMessagesCount: 0,
          };
        }
        return entity;
      });
    });
  };

  return (
    <div>
      <List
        className="rhs-list rhs-channel-list"
        component="nav"
        subheader={
          <ListSubheader component="div" id="nested-list-subheader">
            People
          </ListSubheader>
        }
      >
        <div>
          {users &&
            users.map((user: IOnlineUser) => (
              <ListItem
                button
                className={user.conversation_id && user.conversation_id === internalConvState.active_window?.conversation_id ? 'bg-tc-6' : ''}
                key={user.id}
                onClick={() => initiateConversation(user.id)}
              >
                <ListItemIcon>
                  {/* This list only reflects login/logout (see useOnlineUsers/useSocket USER_JOINED/USER_LEFT),
                      not shift/Break status, so a logged-out user isn't "away" — they're offline. No "rhs-badge"
                      wrapper here on purpose: this class isn't meant to pick up the shared badge colors, it stays
                      an uncolored/blank dot, same as "away" rendered before this rename. */}
                  <Badge className={user.online ? 'available' : 'offline'} variant="dot" />
                </ListItemIcon>
                <ListItemText primary={`${user.display_name}`} />
                {conversation.isLoading && <Loader />}
                {user.conversation_id && (
                  <Badge
                    className="mr-6 badge-design"
                    badgeContent={(internalConvState.ongoing_messages?.[user.conversation_id]?.filter((eachMessage) => eachMessage.isread === false)?.length ?? 0) + (user.unReadMessagesCount || 0)}
                    max={9}
                  />
                )}
              </ListItem>
            ))}
        </div>
      </List>
    </div>
  );
};
